import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/lib/cart";
import { useCartUI } from "@/lib/cartui";
import { useStoreBase } from "@/lib/store";
import { toast } from "sonner";
import { money } from "@/lib/money";
import { trackStoreEvent } from "@/lib/storeAds";
import { findVariant, productHasVariants, variantAxes, variantLabel } from "@/lib/productVariants";

export function useListingAddToCart() {
  const nav = useNavigate();
  const { base } = useStoreBase();
  const { add } = useCart();
  const { openCart, skipCart } = useCartUI();
  const [picking, setPicking] = useState(null);

  const addFromListing = (product, qty = 1) => {
    if (productHasVariants(product)) {
      setPicking({ product, qty: Math.max(1, qty || 1) });
      return;
    }
    add(product, qty);
    trackStoreEvent("add_to_cart", { value: Number(product.price || 0) * qty, currency: "INR", content_ids: [product.id], content_name: product.name });
    if (skipCart) nav(`${base}/checkout`);
    else {
      openCart();
      toast.success("Added to cart");
    }
  };

  const variantModal = (
    <VariantSelectModal
      product={picking?.product || null}
      qty={picking?.qty || 1}
      open={Boolean(picking)}
      onClose={() => setPicking(null)}
      onConfirm={(variant, qty) => {
        const product = picking?.product;
        if (!product || !variant) return;
        add(product, qty, variant);
        trackStoreEvent("add_to_cart", { value: Number(variant.price || product.price || 0) * qty, currency: "INR", content_ids: [product.id], content_name: product.name });
        setPicking(null);
        if (skipCart) nav(`${base}/checkout`);
        else {
          openCart();
          toast.success("Added to cart");
        }
      }}
    />
  );

  return { addFromListing, variantModal };
}

export default function VariantSelectModal({ product, qty = 1, open, onClose, onConfirm }) {
  const { base } = useStoreBase();
  const primary = "var(--store-primary, #15803D)";
  const [variantId, setVariantId] = useState("");
  const variants = product?.variants || [];

  useEffect(() => {
    setVariantId("");
  }, [product?.id, open]);

  const active = useMemo(() => variants.find((v) => v.id === variantId) || null, [variants, variantId]);
  const { sizes, colors, colorHex } = variantAxes(product);
  const price = active?.price ?? product?.price;
  const mrp = active?.mrp ?? product?.mrp;
  const oos = active && active.stock != null && active.stock <= 0;

  const pick = (nextSize, nextColor) => {
    const v = findVariant(product, nextSize, nextColor);
    if (v) setVariantId(v.id);
  };

  const submit = () => {
    if (!active) {
      toast.error("Please select a variation to add to cart");
      return;
    }
    if (oos) {
      toast.error("That variation is out of stock");
      return;
    }
    onConfirm(active, qty);
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="variant-select-modal">
        <DialogHeader>
          <DialogTitle className="pr-6">{product.name}</DialogTitle>
          <DialogDescription>
            Please select a variation to add to cart
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-baseline gap-2">
          <span className="font-mono-nums text-xl font-bold text-stone-900">{money(price, product.currency_symbol || "₹")}</span>
          {mrp && mrp > price ? <span className="font-mono-nums text-sm text-stone-400 line-through">{money(mrp, product.currency_symbol || "₹")}</span> : null}
        </div>

        {sizes.length > 0 ? (
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Size</div>
            <div className="flex flex-wrap gap-2">
              {sizes.map((sz) => {
                const v = findVariant(product, sz, active?.attributes?.color);
                const selected = active?.attributes?.size === sz;
                const disabled = !v || (v.stock != null && v.stock <= 0);
                return (
                  <button
                    key={sz}
                    type="button"
                    disabled={disabled}
                    onClick={() => pick(sz, active?.attributes?.color)}
                    data-testid={`variant-size-${sz}`}
                    className={`px-4 py-2 rounded-full border text-sm font-medium ${selected ? "text-white" : "text-stone-800 bg-white"} ${disabled ? "opacity-40 line-through cursor-not-allowed" : ""}`}
                    style={selected ? { background: primary, borderColor: primary } : { borderColor: "#e7e5e4" }}
                  >
                    {sz}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {colors.length > 0 ? (
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Color</div>
            <div className="flex flex-wrap gap-2">
              {colors.map((col) => {
                const v = findVariant(product, active?.attributes?.size, col);
                const selected = active?.attributes?.color === col;
                const disabled = !v || (v.stock != null && v.stock <= 0);
                return (
                  <button
                    key={col}
                    type="button"
                    disabled={disabled}
                    onClick={() => pick(active?.attributes?.size, col)}
                    data-testid={`variant-color-${col}`}
                    className={`px-4 py-2 rounded-full border text-sm font-medium flex items-center gap-2 ${selected ? "border-stone-900 bg-stone-50" : "border-stone-200 bg-white"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <span className="w-3.5 h-3.5 rounded-full border border-stone-300" style={{ background: colorHex[col] || col.toLowerCase() }} />
                    {col}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {sizes.length === 0 && colors.length === 0 ? (
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">Options</div>
            <div className="flex flex-wrap gap-2">
              {variants.map((v) => {
                const selected = variantId === v.id;
                const disabled = v.stock != null && v.stock <= 0;
                return (
                  <button
                    key={v.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => setVariantId(v.id)}
                    data-testid={`variant-option-${v.id}`}
                    className={`px-4 py-2 rounded-full border text-sm font-medium ${selected ? "text-white" : "text-stone-800 bg-white"} ${disabled ? "opacity-40 line-through cursor-not-allowed" : ""}`}
                    style={selected ? { background: primary, borderColor: primary } : { borderColor: "#e7e5e4" }}
                  >
                    {variantLabel(v)}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <button
            type="button"
            onClick={submit}
            data-testid="variant-select-add"
            className="flex-1 h-11 rounded-lg text-white font-semibold disabled:opacity-50"
            style={{ background: primary }}
          >
            Add to cart
          </button>
          <Link
            to={`${base}/product/${product.slug}?select=variant`}
            onClick={onClose}
            className="flex-1 h-11 rounded-lg border border-stone-200 text-sm font-semibold text-stone-800 inline-flex items-center justify-center"
            data-testid="variant-select-view-product"
          >
            View product
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
}
